import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/nav/PublicNav";
import Footer from "@/components/nav/Footer";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";

function TicketRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest font-bold text-obsidian/60">{label.toUpperCase()}</div>
      <div className="text-obsidian mt-1 font-medium">{value}</div>
    </div>
  );
}

export default function EmployeeDetail() {
  const { slug } = useParams();
  const [emp, setEmp] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/employees/${slug}`).then(({data}) => setEmp(data)).catch(() => {});
  }, [slug]);

  if (!emp) return <div className="min-h-screen bg-ink text-white"><PublicNav /><div className="max-w-7xl mx-auto p-10 text-smoke">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-ink text-white">
      <PublicNav />
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="brand-eyebrow mb-6">{emp.industry?.toUpperCase()}</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <h1 data-testid="detail-name" className="brand-headline text-6xl md:text-9xl">{emp.name}<span className="text-gold">.</span></h1>
              <div className="text-smoke text-lg md:text-xl mt-4">{emp.role_title}</div>
              <p className="text-white/90 text-xl md:text-2xl leading-snug mt-10 max-w-3xl">{emp.description}</p>
              <p className="text-smoke text-base md:text-lg leading-relaxed mt-6 max-w-3xl">{emp.long_description}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <button data-testid="hire-cta" onClick={() => nav(`/signup?employee=${emp.slug}`)} className="btn-gold">Hire {emp.name} <ArrowRight size={18} /></button>
                <Link to="/pricing" className="btn-outline">See Pricing</Link>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="card-white p-8">
                <div className="flex items-center justify-between">
                  <div className="brand-eyebrow" style={{color:'#0B0D10'}}>PREVIEW</div>
                  <div className="text-[10px] tracking-widest text-purple font-semibold">DEMO</div>
                </div>
                <div className="brand-headline text-4xl mt-3">{emp.hero_stat || "Ready to work"}</div>
                <div className="text-ink/70 text-sm mt-4">Example Hired workspace.</div>
                <div className="mt-6 h-px bg-ink/10" />
                <div className="mt-6 space-y-3 text-sm text-ink">
                  <div className="flex items-center gap-2"><span className="dot dot-green" /> Preview environment</div>
                  <div className="flex items-center gap-2"><span className="dot dot-amber" /> Example: items waiting on you</div>
                  <div className="flex items-center gap-2"><span className="dot dot-gray" /> Response time: under 5 min (example)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULE HIGHLIGHT — Foundation First / DigSafe */}
      {emp.slug === "foundation-first" && (
        <section className="border-b border-line">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <div className="brand-eyebrow mb-6">SIGNATURE MODULE · IN DEVELOPMENT</div>
                <h2 className="brand-headline text-5xl md:text-7xl">
                  DigSafe<span className="text-purple italic">.</span>
                </h2>
                <div className="text-steel text-lg mt-4">Louisiana 811 utility-locate assistance — coming soon.</div>
                <p className="text-ivory/85 mt-8 leading-relaxed">
                  Foundation First will help you prepare and track Louisiana 811 DigSafe utility-locate requests, and notify the crew when marks are reported clear. Foundation First facilitates the request; it does not itself guarantee clearance, and does not replace the excavator's legal obligations.
                </p>
                <div className="mt-6 border border-line p-5 bg-graphite/40 text-sm text-steel space-y-2">
                  <div><span className="text-ivory font-semibold">Statutory notice.</span> Louisiana law requires a minimum of <span className="text-gold">2 full business days</span> advance notice for locate responses.</div>
                  <div><span className="text-ivory font-semibold">Training rule.</span> Since <span className="text-gold">August 1, 2025</span>, at least one excavator on each job site must complete Louisiana 811 training annually.</div>
                  <div className="pt-2 border-t border-line"><span className="text-purple font-semibold">Status:</span> DigSafe workflow and permissions are in development. Preview only — not live.</div>
                </div>
                <a href="https://foundation-1st.com" target="_blank" rel="noreferrer" data-testid="digsafe-external" className="mt-8 inline-flex text-purple hover:underline text-sm">foundation-1st.com →</a>
              </div>
              <div className="lg:col-span-7">
                <div className="card-white p-8">
                  <div className="flex items-center justify-between">
                    <div className="brand-eyebrow" style={{color:'#0B0D10'}}>EXAMPLE LOCATE REQUEST · LA 811</div>
                    <div className="text-[11px] tracking-widest font-bold text-purple">PREVIEW</div>
                  </div>
                  <div className="brand-headline text-3xl mt-3 text-obsidian">Draft ticket · Not filed</div>
                  <div className="grid grid-cols-2 gap-6 mt-8 text-sm text-obsidian">
                    <TicketRow label="Parish" value="Orleans" />
                    <TicketRow label="Address" value="2801 Magazine St" />
                    <TicketRow label="Work Type" value="Foundation trenching" />
                    <TicketRow label="Depth" value="36 in" />
                    <TicketRow label="Min. notice" value="2 full business days" />
                    <TicketRow label="Status" value="Draft — awaiting excavator review" />
                  </div>
                  <div className="mt-8 border-t pt-6" style={{borderColor:'#e5e0d3'}}>
                    <div className="flex items-center gap-3">
                      <span className="dot dot-amber" />
                      <div className="text-obsidian text-sm font-semibold">Example only. Live LA 811 submissions require the qualified excavator to submit and confirm.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TASKS */}
      <section id="tasks" className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="brand-eyebrow mb-6">WHAT {emp.name?.toUpperCase()} HANDLES</div>
          <h2 className="brand-headline text-5xl md:text-7xl mb-14 max-w-3xl">Every day.<br /><span className="text-gold">Without asking.</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(emp.tasks || []).map((t, i) => (
              <div key={i} className="border border-line p-8 hover:border-gold transition-colors">
                <div className="text-gold text-xs font-mono">{String(i+1).padStart(2,'0')}</div>
                <div className="brand-headline text-2xl mt-4">{t.title}</div>
                <div className="text-smoke text-sm mt-3">{t.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERMISSIONS - You're still the boss */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <div className="brand-eyebrow mb-6">CONTROL</div>
              <h2 className="brand-headline text-5xl md:text-7xl">You're still<br /><span className="text-gold">the boss.</span></h2>
              <p className="text-smoke text-lg mt-8 max-w-lg">Every task has a rule. Auto, needs approval, or off — you decide. Change your mind anytime.</p>
              <div className="mt-10 flex items-start gap-3">
                <ShieldCheck className="text-gold shrink-0" size={22} />
                <div>
                  <div className="text-white font-semibold">Human-in-the-Loop by default</div>
                  <div className="text-smoke text-sm">Sensitive actions wait for you to approve. Nothing goes out that shouldn't.</div>
                </div>
              </div>
            </div>
            <div className="card-white p-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink/60 text-[11px] tracking-widest uppercase">
                    <th className="pb-4">Task</th>
                    <th className="pb-4">Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {(emp.default_permissions || []).map((p, i) => (
                    <tr key={i} className="border-t" style={{borderColor:'#e5e5e5'}}>
                      <td className="py-3 text-ink">{p.task_label}</td>
                      <td className="py-3">
                        <span className={`text-[11px] tracking-widest font-bold ${p.mode==='AUTO' ? 'text-emerald-700' : p.mode==='DISABLED' ? 'text-red-700' : 'text-amber-700'}`}>
                          {p.mode}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="brand-headline text-5xl md:text-7xl">Put {emp.name} to work.</h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <button data-testid="cta-hire-bottom" onClick={() => nav(`/signup?employee=${emp.slug}`)} className="btn-gold">Hire {emp.name} <ArrowRight size={18} /></button>
            <Link to="/pricing" className="btn-outline">See Pricing</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
